import { createLogger } from '../utils/logger';

const log = createLogger('CandlestickTracker');

const MAX_CANDLES = 300;

type Candle = { x: Date; y: [number, number, number, number] };
type AllCandles = Record<string, Candle[]>;

class CandlestickTracker {
    private tickerCandlesticks: Map<string, Candle[]>;
    private subscribers: Set<(data: AllCandles) => void>;

    constructor() {
        this.tickerCandlesticks = new Map();
        this.subscribers = new Set();
    }

    insertCandle(
        ticker: string,
        ohlc: {
            open: number;
            high: number;
            low: number;
            close: number;
            timestamp: string | number | Date;
        },
    ) {
        if (!this.tickerCandlesticks.has(ticker)) {
            this.tickerCandlesticks.set(ticker, []);
        }

        const candles = this.tickerCandlesticks.get(ticker)!;
        candles.push({
            x: new Date(ohlc.timestamp),
            y: [ohlc.open, ohlc.high, ohlc.low, ohlc.close],
        });

        // Keep only the most recent MAX_CANDLES candles to avoid unbounded growth
        if (candles.length > MAX_CANDLES) {
            candles.splice(0, candles.length - MAX_CANDLES);
        }

        log.debug('Inserted candlestick', { ticker, ohlc });

        this.notifySubscribers();
    }

    getAllCandlestickData(): AllCandles {
        const result: AllCandles = {};
        this.tickerCandlesticks.forEach((data, ticker) => {
            result[ticker] = data;
        });
        return result;
    }

    getCandlestickData(ticker: string): Candle[] {
        return this.tickerCandlesticks.get(ticker) || [];
    }

    getLatestCandle(ticker: string): Candle | null {
        const candles = this.tickerCandlesticks.get(ticker);
        return candles && candles.length > 0 ? candles[candles.length - 1] : null;
    }

    subscribe(callback: (data: AllCandles) => void) {
        this.subscribers.add(callback);
    }

    unsubscribe(callback: (data: AllCandles) => void) {
        this.subscribers.delete(callback);
    }

    notifySubscribers() {
        this.subscribers.forEach((callback) => callback(this.getAllCandlestickData()));
    }
}

const candlestickTracker = new CandlestickTracker();
export default candlestickTracker;
